import { useState, useMemo } from 'react';
import { Link } from 'react-router';
import {
    ChevronLeft,
    ChevronRight,
    Check,
    Clock,
    Users,
    Calendar,
    UtensilsCrossed,
    BookOpen,
    MessageCircle,
    RefreshCw,
    Plus,
    SkipForward,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMyProgram, useMyMenu, useMarkMealComplete, type MenuMeal } from '@/services/programs';
import { useMyPlan } from '@/services/planShopping';
import { ShopForPlanPanel, StartsInBadge } from '@/components/ShopForPlanCard';
import { formatLongDate } from '@/lib/planShopping';
import { useAuthStore } from '@/store/useAuthStore';
import { toLocalDateStr, fromLocalDateStr } from '@/lib/date';
import { Feature } from '@/types/auth';
import { RecipeImage } from '@/components/RecipeImage';
import toast from 'react-hot-toast';

const MEAL_TYPE_ORDER = ['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK'] as const;
const MEAL_TYPE_LABELS: Record<string, string> = {
    BREAKFAST: 'Breakfast',
    LUNCH: 'Lunch',
    DINNER: 'Dinner',
    SNACK: 'Snack',
};
const MEAL_TYPE_ICONS: Record<string, string> = {
    BREAKFAST: '🌅',
    LUNCH: '☀️',
    DINNER: '🌙',
    SNACK: '🍎',
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole calendar days from a to b (both local midnights). */
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / DAY_MS);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * "Cook 4 servings" for a meal whose menu servings are 4. Those servings are
 * how much to cook, mostly the recipe's yield, and ticking the meal scores the
 * portion eaten (one serving unless the coach prescribed more), so the card no
 * longer says "4 servings" as if that were the portion (#394).
 */
const servingsLabel = (meal: Pick<MenuMeal, 'servings' | 'servingsEaten'>) => {
    const cook = meal.servings || 1;
    const eat = meal.servingsEaten || 1;
    const cookText = `Cook ${plural(cook, 'serving')}`;
    return eat === 1 ? cookText : `${cookText}, your portion ${plural(eat, 'serving')}`;
};

const MyMenu = () => {
    // null until the user picks a day: then it opens on today, or on Day 1
    // when the plan has not started yet (#330).
    const [pickedDate, setSelectedDate] = useState<Date | null>(null);
    const { hasFeature } = useAuthStore();

    const { data: assignment, isLoading: programLoading } = useMyProgram();
    const { data: plan, isLoading: planLoading } = useMyPlan();
    const markCompleteMutation = useMarkMealComplete();

    // Before day one the weeks run from the plan's Day 1 (Day 1 to 7, 8 to
    // 14, ...) instead of Sunday to Saturday, so the menu opens on Day 1.
    const planStartStr = plan?.phase === 'UPCOMING' ? plan.startDate : null;
    const planStart = useMemo(
        () => (planStartStr ? fromLocalDateStr(planStartStr) : null),
        [planStartStr],
    );
    const selectedDate = useMemo(() => {
        if (pickedDate) return pickedDate;
        if (planStart) return planStart;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }, [pickedDate, planStart]);

    // Calculate date range for the week
    const dateRange = useMemo(() => {
        const start = new Date(selectedDate);
        if (planStart) {
            const weekIndex = Math.floor(daysBetween(planStart, selectedDate) / 7);
            start.setTime(planStart.getTime());
            start.setDate(planStart.getDate() + weekIndex * 7);
        } else {
            start.setDate(start.getDate() - start.getDay()); // Start of week (Sunday)
        }
        const end = new Date(start);
        end.setDate(end.getDate() + 6); // End of week (Saturday, or Day 7 of the plan week)
        return {
            startDate: toLocalDateStr(start),
            endDate: toLocalDateStr(end),
        };
    }, [selectedDate, planStart]);

    /** Day N of the plan for a date, or null outside it (upcoming plans only). */
    const planDayNumber = (date: Date) => {
        if (!planStart || !plan) return null;
        const n = daysBetween(planStart, date) + 1;
        return n >= 1 && n <= plan.duration ? n : null;
    };

    const { data: menuData = [], isLoading: menuLoading } = useMyMenu(
        dateRange.startDate,
        dateRange.endDate,
    );

    // Get meals for selected date
    const selectedDateStr = toLocalDateStr(selectedDate);
    const todayMenu = useMemo(() => {
        return menuData.find((d) => d.date === selectedDateStr);
    }, [menuData, selectedDateStr]);

    // Group meals by type
    const mealsByType = useMemo(() => {
        if (!todayMenu?.meals) return {};
        const grouped: Record<string, typeof todayMenu.meals> = {};
        for (const meal of todayMenu.meals) {
            const type = meal.mealType;
            if (!grouped[type]) grouped[type] = [];
            grouped[type].push(meal);
        }
        return grouped;
    }, [todayMenu]);

    const navigateWeek = (direction: 'prev' | 'next') => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        setSelectedDate(newDate);
    };

    const navigateDay = (direction: 'prev' | 'next') => {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
        setSelectedDate(newDate);
    };

    const toggleMealComplete = async (
        mealId: string,
        mealType: 'program' | 'custom',
        isCompleted: boolean,
    ) => {
        try {
            const result = await markCompleteMutation.mutateAsync({
                mealId,
                mealType,
                scheduledDate: selectedDateStr,
                isCompleted: !isCompleted,
            });

            if (isCompleted) {
                toast.success('Meal unmarked');
                return;
            }

            const points = result?.pointsEarned;
            if (typeof points === 'number' && points > 0) {
                toast.success(`Meal completed! +${Math.round(points)} points`);
            } else {
                toast.success('Meal completed!');
            }
        } catch {
            toast.error('Failed to update meal');
        }
    };

    // Calculate daily progress
    const dailyProgress = useMemo(() => {
        if (!todayMenu?.meals?.length) return { completed: 0, total: 0, percentage: 0 };
        const completed = todayMenu.meals.filter((m) => m.isCompleted).length;
        const total = todayMenu.meals.length;
        return {
            completed,
            total,
            percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
        };
    }, [todayMenu]);

    // Generate week days for navigation
    const weekDays = useMemo(() => {
        const days = [];
        const start = fromLocalDateStr(dateRange.startDate);
        for (let i = 0; i < 7; i++) {
            const date = new Date(start);
            date.setDate(start.getDate() + i);
            days.push(date);
        }
        return days;
    }, [dateRange.startDate]);

    if (programLoading || planLoading) {
        return (
            <div className="max-w-4xl mx-auto px-6 py-8">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading your program...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!assignment) {
        return (
            <div className="max-w-4xl mx-auto px-6 py-8">
                <div className="bg-card rounded-3xl p-12 shadow-lg text-center">
                    <UtensilsCrossed className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
                    <h2 className="text-2xl font-bold text-foreground mb-3">No Active Program</h2>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                        You don't have an active meal program yet. Contact your coach to get started
                        with a personalized nutrition plan.
                    </p>
                    <Link to="/recipes">
                        <Button>
                            <BookOpen className="w-4 h-4 mr-2" />
                            Browse Recipes
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    const program = assignment.program;
    const programStartDate = new Date(assignment.startDate);
    const upcoming = plan?.phase === 'UPCOMING';
    const weekStartDay = planDayNumber(fromLocalDateStr(dateRange.startDate));

    return (
        <div className="max-w-4xl mx-auto px-6 py-8">
            {/* Program Header */}
            <div className="bg-brand-header rounded-3xl p-6 mb-6 text-brand-header-ink">
                <div className="flex items-start justify-between">
                    <div>
                        {upcoming && plan && (
                            <div className="mb-3">
                                <StartsInBadge days={plan.daysUntilStart} />
                            </div>
                        )}
                        <h1 className="text-2xl font-bold mb-2">{program.name}</h1>
                        {program.description && (
                            <p className="text-brand-header-ink/80 text-sm mb-3">
                                {program.description}
                            </p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                            <span className="flex items-center gap-1 whitespace-nowrap">
                                <Calendar className="w-4 h-4" />
                                {upcoming && plan
                                    ? `Starts ${formatLongDate(plan.startDate)}`
                                    : `Started ${programStartDate.toLocaleDateString()}`}
                            </span>
                            <span className="flex items-center gap-1 whitespace-nowrap">
                                <Clock className="w-4 h-4" />
                                {program.duration} days
                            </span>
                        </div>
                    </div>
                    {program.imageUrl && (
                        <RecipeImage
                            src={program.imageUrl}
                            alt={program.name}
                            variant="thumb"
                            thumbSize={80}
                            className="w-20 h-20 rounded-xl object-cover"
                        />
                    )}
                </div>
                {plan && hasFeature(Feature.SHOPPING_LISTS) && <ShopForPlanPanel plan={plan} />}
            </div>

            {/* Week Navigation */}
            <div className="bg-card rounded-2xl p-4 mb-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <Button variant="outline" size="sm" onClick={() => navigateWeek('prev')}>
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <div className="flex items-center gap-3">
                        {upcoming ? (
                            <h2 className="text-lg">
                                {weekStartDay && (
                                    <span className="font-semibold">
                                        Week {Math.floor((weekStartDay - 1) / 7) + 1}
                                        <span className="font-normal text-muted-foreground">
                                            {' · '}
                                        </span>
                                    </span>
                                )}
                                <span className="text-muted-foreground">
                                    {fromLocalDateStr(dateRange.startDate).getDate()} to{' '}
                                    {fromLocalDateStr(dateRange.endDate).toLocaleDateString(
                                        'en-GB',
                                        { day: 'numeric', month: 'short' },
                                    )}
                                </span>
                            </h2>
                        ) : (
                            <h2 className="text-lg font-semibold">
                                {new Date(dateRange.startDate).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                })}{' '}
                                -{' '}
                                {new Date(dateRange.endDate).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                })}
                            </h2>
                        )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => navigateWeek('next')}>
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>

                {/* Day Selector */}
                <div className={`grid grid-cols-7 ${upcoming ? 'gap-1 sm:gap-2' : 'gap-2'}`}>
                    {weekDays.map((date) => {
                        const dateStr = toLocalDateStr(date);
                        const isSelected = dateStr === selectedDateStr;
                        const isToday = dateStr === toLocalDateStr(new Date());
                        const hasMenu = menuData.some(
                            (d) => d.date === dateStr && d.meals.length > 0,
                        );
                        const dayNumber = planDayNumber(date);

                        return (
                            <button
                                key={dateStr}
                                onClick={() => setSelectedDate(date)}
                                className={`${upcoming ? 'px-0 py-2 sm:p-2' : 'p-2'} rounded-xl text-center transition-all ${
                                    isSelected
                                        ? 'bg-brand-header text-brand-header-ink'
                                        : isToday
                                          ? 'bg-brand-header-wash text-brand-text-blue'
                                          : hasMenu
                                            ? 'bg-muted text-foreground hover:bg-brand-header-wash'
                                            : 'text-muted-foreground'
                                }`}
                            >
                                {dayNumber && (
                                    <div className="text-[11px] sm:text-xs font-bold whitespace-nowrap">
                                        Day {dayNumber}
                                    </div>
                                )}
                                <div className="text-xs font-medium">
                                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                                </div>
                                <div className="text-lg font-bold">{date.getDate()}</div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Daily Progress Card */}
            <div className="bg-card rounded-2xl p-4 mb-6 shadow-sm">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold text-foreground">
                            {selectedDate.toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                            })}
                        </h3>
                        {todayMenu?.dayName && (
                            <p className="text-sm text-muted-foreground">{todayMenu.dayName}</p>
                        )}
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-brand-text-blue">
                            {dailyProgress.completed}/{dailyProgress.total}
                        </div>
                        <div className="text-xs text-muted-foreground">Meals Done</div>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="mt-4">
                    <div className="h-2 bg-brand-bar-track rounded-full overflow-hidden">
                        <div
                            className={`h-full bg-brand-bar-fill transition-all duration-500 ${
                                dailyProgress.percentage > 0 ? 'border border-brand-bar-edge' : ''
                            }`}
                            style={{ width: `${dailyProgress.percentage}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Day Navigation */}
            <div className="flex items-center justify-between mb-4">
                <Button variant="outline" size="sm" onClick={() => navigateDay('prev')}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Previous Day
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigateDay('next')}>
                    Next Day <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
            </div>

            {/* Meals List */}
            {menuLoading ? (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-header mx-auto"></div>
                </div>
            ) : !todayMenu?.meals?.length ? (
                <div className="bg-card rounded-2xl p-8 text-center shadow-sm">
                    <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No meals scheduled for this day.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {MEAL_TYPE_ORDER.map((mealType) => {
                        const meals = mealsByType[mealType];
                        if (!meals?.length) return null;

                        return (
                            <div key={mealType}>
                                <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground mb-3">
                                    <span>{MEAL_TYPE_ICONS[mealType]}</span>
                                    {MEAL_TYPE_LABELS[mealType]}
                                </h3>
                                <div className="space-y-3">
                                    {meals.map((meal) => {
                                        // Skip meals that are marked as skipped
                                        if (meal.isSkipped) {
                                            return (
                                                <div
                                                    key={`${meal.id}-${meal.scheduledDate}`}
                                                    /* opacity-60 here took the
                                                       muted text inside below
                                                       3:1. Skipped is already
                                                       said by the strikethrough,
                                                       the icon and the sentence,
                                                       so the card does not also
                                                       need to be faded out of
                                                       readability. */
                                                    className="bg-muted rounded-2xl p-4 shadow-sm border-2 border-border"
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <SkipForward className="w-6 h-6 text-muted-foreground" />
                                                        <div className="flex-1">
                                                            <h4 className="font-medium text-muted-foreground line-through">
                                                                {meal.recipe?.name ||
                                                                    'Meal skipped'}
                                                            </h4>
                                                            <p className="text-xs text-muted-foreground">
                                                                This meal has been skipped by your
                                                                coach
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div
                                                key={`${meal.id}-${meal.scheduledDate}`}
                                                className={`bg-card rounded-2xl p-4 shadow-sm border-2 transition-all ${
                                                    meal.isCompleted
                                                        ? 'border-brand-text-green'
                                                        : 'border-transparent hover:border-border'
                                                }`}
                                            >
                                                {/* Customization badges */}
                                                {(meal.isSubstituted || meal.isCustomMeal) && (
                                                    <div className="flex flex-wrap gap-2 mb-3">
                                                        {meal.isSubstituted && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-fill-orange text-brand-fill-ink text-xs font-medium">
                                                                <RefreshCw className="w-3 h-3" />
                                                                Substituted
                                                            </span>
                                                        )}
                                                        {meal.isCustomMeal && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-fill-lime text-brand-fill-ink text-xs font-medium">
                                                                <Plus className="w-3 h-3" />
                                                                Custom Meal
                                                            </span>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="flex items-start gap-4">
                                                    {/* Recipe Image */}
                                                    <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-muted">
                                                        {meal.recipe?.imageUrl ? (
                                                            <RecipeImage
                                                                src={meal.recipe.imageUrl}
                                                                alt={meal.recipe.name}
                                                                variant="thumb"
                                                                thumbSize={80}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                                                <UtensilsCrossed className="w-8 h-8" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Recipe Info */}
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="font-semibold text-foreground mb-1">
                                                            {meal.recipe?.name || 'Unknown Recipe'}
                                                        </h4>

                                                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-2">
                                                            {/* The menu's servings are how much to
                                                               cook (mostly the recipe's yield), not
                                                               a portion, so they read as a cooking
                                                               amount. The portion is shown only when
                                                               it is not the plain one serving (#394). */}
                                                            <span className="flex items-center gap-1">
                                                                <Users className="w-3 h-3" />
                                                                {servingsLabel(meal)}
                                                            </span>
                                                            {(meal.recipe?.prepTime ||
                                                                meal.recipe?.cookTime) && (
                                                                <span className="flex items-center gap-1">
                                                                    <Clock className="w-3 h-3" />
                                                                    {(meal.recipe.prepTime || 0) +
                                                                        (meal.recipe.cookTime ||
                                                                            0)}{' '}
                                                                    min
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Coach Notes */}
                                                        {meal.coachNotes && (
                                                            <div className="flex items-start gap-2 mt-2 p-2 bg-brand-header-wash rounded-lg border border-border">
                                                                <MessageCircle className="w-4 h-4 text-brand-text-blue shrink-0 mt-0.5" />
                                                                <p className="text-xs text-brand-text-blue">
                                                                    <span className="font-medium">
                                                                        Coach Note:
                                                                    </span>{' '}
                                                                    {meal.coachNotes}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Original recipe note (for substitutions) */}
                                                        {meal.originalRecipe &&
                                                            meal.isSubstituted && (
                                                                <p className="text-xs text-muted-foreground mt-1 italic">
                                                                    Originally:{' '}
                                                                    {meal.originalRecipe.name}
                                                                </p>
                                                            )}

                                                        {/* Regular notes */}
                                                        {meal.notes && !meal.coachNotes && (
                                                            <p className="text-xs text-muted-foreground italic mt-1">
                                                                {meal.notes}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Complete Button */}
                                                    <button
                                                        onClick={() =>
                                                            toggleMealComplete(
                                                                meal.id,
                                                                meal.type ?? 'program',
                                                                meal.isCompleted,
                                                            )
                                                        }
                                                        disabled={markCompleteMutation.isPending}
                                                        className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all shrink-0 ${
                                                            meal.isCompleted
                                                                ? 'bg-brand-fill-green text-brand-fill-ink'
                                                                : 'bg-muted text-muted-foreground hover:bg-brand-header-wash'
                                                        }`}
                                                    >
                                                        <Check className="w-6 h-6" />
                                                    </button>
                                                </div>

                                                {/* View Recipe Link */}
                                                <div className="mt-3 pt-3 border-t border-border">
                                                    <Link
                                                        to={`/recipes/${meal.recipeId}`}
                                                        className="text-sm text-brand-text-blue  font-medium"
                                                    >
                                                        View Recipe Instructions →
                                                    </Link>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MyMenu;
